import { Link } from "react-router-dom";
import type { HypnogramResult } from "@rekonas/data-access";
import type { OperationalRecordingDetail } from "@rekonas/domain";
import type { HypnogramViewerState } from "@rekonas/signal-viewer";
import { Alert, Spinner } from "@rekonas/ui";
import { ResultHypnogramPlot } from "../components/hypnogram/ResultHypnogramPlot";

export function SleepStagingIdentity({ recording, result, loading, error, canManage }: {
  recording: OperationalRecordingDetail;
  result?: HypnogramResult;
  loading: boolean;
  error: boolean;
  canManage: boolean;
}) {
  const incompatible = recording.current_analysis && !recording.current_analysis.compatible;
  const run = result?.analysis_run_ref;
  const retained = !run || run.startsWith("legacy_");
  return <section className="sleep-staging-identity" aria-label="Sleep staging identity">
    <strong>Sleep staging</strong>{" · "}
    {incompatible ? "Promoted result does not match the viewed signals; stages are unavailable." :
      recording.hypnogram === "unsupported_mapping" ? "Unavailable · no compatible EEG." :
      recording.hypnogram === "failed" ? "Unavailable · processing failed." :
      loading ? <span><Spinner /> Resolving displayed result…</span> :
      error || !result || result.state !== "available" ? "Displayed result unavailable; refresh the recording." : <>
        <span>{result.algorithm_release?.display_name || result.model?.id || "Retained pipeline result"}</span>
        {result.algorithm_release?.release_id && <> · release <code>{result.algorithm_release.release_id}</code></>}
        {run && !retained && <> · run <abbr title={run}><code>{run.slice(0, 17)}…</code></abbr></>}
        {retained ? <> · retained pipeline result</> : <> · {result.explicitly_promoted ? "Promoted / current" : "Current"}</>}
        <details><summary>Result identity and provenance</summary>
          <p>{run && !retained ? <>Analysis run: <code>{run}</code></> : "No analysis-run ID is available for this retained pipeline result."}</p>
          <p>Artifact: <code>{result.data_version || recording.data_version}</code></p>
          {result.finished_at && <p>Completed: {result.finished_at}</p>}
          {result.promoted_at && !retained && <p>Promoted: {result.promoted_at}</p>}
          {canManage && <Link to="/internal/analysis-runs">View analysis history</Link>}
        </details>
      </>}
  </section>;
}

export function OperationalHypnogramCard({ recording, result, loading, error, viewer }: {
  recording: OperationalRecordingDetail;
  result?: HypnogramResult;
  loading: boolean;
  error: boolean;
  viewer: HypnogramViewerState;
}) {
  const caption = `${viewer.fullNight ? "Full night" : "Selected viewer window"} · elapsed recording time`;
  const unavailable = recording.current_analysis && !recording.current_analysis.compatible ?
    <Alert>The promoted sleep-staging result belongs to a different scientific data version. Stages are unavailable here.</Alert> :
    recording.hypnogram === "unsupported_mapping" ? <Alert>Sleep stage is unavailable because compatible EEG was not recorded.</Alert> :
    recording.hypnogram === "failed" ? <Alert>Sleep analysis failed for this recording. Signal viewing remains available.</Alert> :
    loading ? <p role="status"><Spinner /> Loading the displayed hypnogram…</p> :
    error || !result || result.state !== "available" ? <Alert>The displayed hypnogram is unavailable. Refresh the recording and try again.</Alert> : null;
  return <section className="operational-hypnogram-card" aria-label="Recording hypnogram">
    {unavailable ? <><h2>Hypnogram</h2><p className="operator-muted">{caption}</p>{unavailable}</> :
      <ResultHypnogramPlot result={result!} durationSeconds={recording.duration_seconds || 0}
        heading="Hypnogram" caption={caption}
        highlight={{ start: viewer.visibleStartSeconds, end: viewer.visibleEndSeconds, full: viewer.fullNight }}
        cursorSeconds={viewer.displayedSeconds} onCursorChange={viewer.onPreview}
        onSeek={viewer.onSeek} />}
  </section>;
}
